// Additive "hologram" surfaces for gameplay volumes: hill zones, bomb sites and jump pads.
// One small shader with four modes; all materials share a single time uniform.

import * as THREE from "three";
import type { MapJumpPad } from "./mapTypes";

/** Seconds, advanced by the Renderer every frame. */
export const zoneTime = new THREE.Uniform(0);

const VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vWorld;
void main() {
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform vec3 uColor;
uniform vec2 uSize;
uniform vec2 uDir;
uniform float uPulse;
uniform float uOpacity;
varying vec2 vUv;
varying vec3 vWorld;

void main() {
  float a = 0.0;
  float pulse = 1.0 - uPulse * (0.5 - 0.5 * sin(uTime * 7.0));
#if MODE == 0
  // Floor of a zone: bright rim, faint scrolling stripes inside.
  float e = min(min(vUv.x, 1.0 - vUv.x) * uSize.x, min(vUv.y, 1.0 - vUv.y) * uSize.y);
  float rim = 1.0 - smoothstep(0.0, 0.4, e);
  float stripes = smoothstep(0.35, 0.5, abs(fract((vUv.x * uSize.x + vUv.y * uSize.y) * 0.35 - uTime * 0.12) - 0.5) * 2.0);
  a = rim * 0.95 + 0.07 + stripes * 0.06;
#elif MODE == 1
  // Curtain: strongest at the bottom, fading up, with slow rising bands.
  float h = 1.0 - vUv.y;
  float bands = 0.75 + 0.25 * sin((vWorld.y * 2.2 - uTime * 1.1) * 3.14159);
  a = pow(h, 1.7) * 0.55 * bands + (1.0 - smoothstep(0.0, 0.05, vUv.y)) * 0.5;
  a += (1.0 - smoothstep(0.0, 0.03, 1.0 - vUv.y)) * 0.25;
#elif MODE == 2
  // Jump pad: chevrons running along the launch direction (rings for vertical launches).
  vec2 p = (vUv - 0.5) * uSize;
  float c;
  if (dot(uDir, uDir) > 0.01) {
    vec2 d = normalize(uDir);
    float along = dot(p, d);
    float across = dot(p, vec2(-d.y, d.x));
    c = fract((along + abs(across) * 0.9) * 0.9 - uTime * 1.3);
  } else {
    c = fract(length(p) * 1.1 - uTime * 1.1);
  }
  float band = smoothstep(0.0, 0.12, c) * (1.0 - smoothstep(0.38, 0.5, c));
  float edge = min(min(vUv.x, 1.0 - vUv.x) * uSize.x, min(vUv.y, 1.0 - vUv.y) * uSize.y);
  a = band * 0.9 + (1.0 - smoothstep(0.0, 0.18, edge)) * 0.8 + 0.08;
#else
  // Beam: vertical gradient with rising streaks.
  float h = 1.0 - vUv.y;
  float streak = 0.55 + 0.45 * sin(vWorld.y * 3.0 - uTime * 5.0 + vUv.x * 9.0);
  a = pow(h, 1.4) * 0.34 * streak * (1.0 - pow(abs(vUv.x - 0.5) * 2.0, 2.0));
#endif
  a *= pulse * uOpacity;
  gl_FragColor = vec4(uColor, a);
  if (a < 0.004) discard;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export type ZoneMode = "floor" | "wall" | "pad" | "beam";
const MODE_INDEX: Record<ZoneMode, number> = { floor: 0, wall: 1, pad: 2, beam: 3 };

export function createZoneMaterial(mode: ZoneMode): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    defines: { MODE: MODE_INDEX[mode] },
    uniforms: {
      uTime: zoneTime,
      uColor: { value: new THREE.Color(1, 1, 1) },
      uSize: { value: new THREE.Vector2(1, 1) },
      uDir: { value: new THREE.Vector2(0, 0) },
      uPulse: { value: 0 },
      uOpacity: { value: 1 },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: false,
  });
}

/** HDR tint so the rims catch the bloom. */
export function setZoneColor(mat: THREE.ShaderMaterial, hex: string, gain = 1.6): void {
  (mat.uniforms.uColor.value as THREE.Color).set(hex).multiplyScalar(gain);
}

/**
 * Floor rectangle plus four curtains around an axis-aligned volume. Owns two materials; the
 * geometry is shared unit planes.
 */
export class ZoneVolume {
  readonly group = new THREE.Group();
  private readonly floorMat = createZoneMaterial("floor");
  private readonly wallMat = createZoneMaterial("wall");
  private static unit: THREE.PlaneGeometry | null = null;

  constructor(
    min: readonly [number, number, number],
    max: readonly [number, number, number],
    wallHeight: number,
    name: string,
  ) {
    const geo = (ZoneVolume.unit ??= new THREE.PlaneGeometry(1, 1));
    const w = max[0] - min[0];
    const d = max[2] - min[2];
    const cx = (min[0] + max[0]) / 2;
    const cz = (min[2] + max[2]) / 2;
    const h = Math.min(Math.max(max[1] - min[1], 1.5), wallHeight);
    this.group.name = name;

    const floor = new THREE.Mesh(geo, this.floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.scale.set(w, d, 1);
    floor.position.set(cx, min[1] + 0.04, cz);
    this.group.add(floor);
    (this.floorMat.uniforms.uSize.value as THREE.Vector2).set(w, d);

    const mk = (sx: number, px: number, pz: number, ry: number): void => {
      const m = new THREE.Mesh(geo, this.wallMat);
      m.scale.set(sx, h, 1);
      m.position.set(px, min[1] + h / 2, pz);
      m.rotation.y = ry;
      this.group.add(m);
    };
    mk(w, cx, min[2], 0);
    mk(w, cx, max[2], 0);
    mk(d, min[0], cz, Math.PI / 2);
    mk(d, max[0], cz, Math.PI / 2);
    for (const o of this.group.children) {
      o.renderOrder = 15;
    }
  }

  setStyle(hex: string, pulse: number): void {
    setZoneColor(this.floorMat, hex);
    setZoneColor(this.wallMat, hex, 1.3);
    this.floorMat.uniforms.uPulse.value = pulse;
    this.wallMat.uniforms.uPulse.value = pulse;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.floorMat.dispose();
    this.wallMat.dispose();
  }
}

export interface PadVisual {
  group: THREE.Group;
  dispose(): void;
}

/** Dark plate with animated chevrons and a faint light beam, sized to the pad volume. */
export function createJumpPadVisual(pad: MapJumpPad, accentHex = "#1fb5c9"): PadVisual {
  const w = Math.max(pad.max[0] - pad.min[0], 0.5);
  const d = Math.max(pad.max[2] - pad.min[2], 0.5);
  const cx = (pad.min[0] + pad.max[0]) / 2;
  const cz = (pad.min[2] + pad.max[2]) / 2;
  const group = new THREE.Group();
  group.name = "jumpPad";

  const baseGeo = new THREE.BoxGeometry(w, 0.06, d);
  const baseMat = new THREE.MeshStandardMaterial({
    color: 0x2a2b2e,
    roughness: 0.55,
    metalness: 0.3,
    emissive: new THREE.Color(accentHex),
    emissiveIntensity: 0.25,
  });
  const base = new THREE.Mesh(baseGeo, baseMat);
  base.position.set(cx, pad.min[1] + 0.03, cz);
  base.receiveShadow = true;
  group.add(base);

  const planeGeo = new THREE.PlaneGeometry(1, 1);
  const padMat = createZoneMaterial("pad");
  setZoneColor(padMat, accentHex, 1.8);
  (padMat.uniforms.uSize.value as THREE.Vector2).set(w, d);
  const lx = pad.launch[0];
  const lz = pad.launch[2];
  const horiz = Math.hypot(lx, lz);
  if (horiz > 0.25 * Math.abs(pad.launch[1]) && horiz > 1) {
    // After the -90 degree X rotation the plane's local +X is world +X and local +Y is world -Z.
    (padMat.uniforms.uDir.value as THREE.Vector2).set(lx / horiz, -lz / horiz);
  }
  const overlay = new THREE.Mesh(planeGeo, padMat);
  overlay.rotation.x = -Math.PI / 2;
  overlay.scale.set(w, d, 1);
  overlay.position.set(cx, pad.min[1] + 0.075, cz);
  overlay.renderOrder = 15;
  group.add(overlay);

  const beamH = Math.min(Math.max(pad.launch[1] * 0.45, 2.5), 6);
  const beamMat = createZoneMaterial("beam");
  setZoneColor(beamMat, accentHex, 1.5);
  const bw = Math.min(w, d) * 0.9;
  for (const ry of [0, Math.PI / 2]) {
    const beam = new THREE.Mesh(planeGeo, beamMat);
    beam.scale.set(bw, beamH, 1);
    beam.position.set(cx, pad.min[1] + 0.07 + beamH / 2, cz);
    beam.rotation.y = ry;
    beam.renderOrder = 15;
    group.add(beam);
  }

  return {
    group,
    dispose(): void {
      group.removeFromParent();
      baseGeo.dispose();
      baseMat.dispose();
      planeGeo.dispose();
      padMat.dispose();
      beamMat.dispose();
    },
  };
}
